/** @format */

import { useEffect, useState } from "react";
import { FiMail } from "react-icons/fi";
import {
  LuBuilding2,
  LuCalendar,
  LuMapPin,
  LuUser,
  LuArrowLeft,
  LuArrowRight,
} from "react-icons/lu";
import { LiaIdCard } from "react-icons/lia";
import toast from "react-hot-toast";
import axios from "axios";

import { AuthButton } from "../common/AuthButton";
import TextInput from "./TextInput";
import PasswordInput from "./PasswordInput";
import PhoneNumberInput from "./PhoneNumberInput";
import { getPasswordStrength } from "../../utils/PasswordStrength";
import { validateEmail } from "../../utils/validation";
import { useAuthStore } from "../../context/useAuthStore";
import SelectInput from "./SelectInput";
import { PhoneNumberUtil, PhoneNumberFormat } from "google-libphonenumber";
import TCModal from "./TCModal";

const phoneUtil = PhoneNumberUtil.getInstance();

interface RegisterFormProps {
  onSuccess?: () => void;
}

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
const STUDENT_ROLE_ID = import.meta.env.VITE_STUDENT_ID;

type FormStep = "personal" | "college" | "password";

interface FormData {
  fullName: string;
  emailId: string;
  phoneNumber: string;
  address: string;
  yearOfStudy: string;
  college: string;
  rollNumber: string;
  password: string;
  confirmPassword?: string;
  roleId: string;
  userId: string;
  tcStatus: "true" | "false";
}

export const RegisterForm: React.FC<RegisterFormProps> = ({ onSuccess }) => {
  const { register } = useAuthStore();

  const [colleges, setColleges] = useState<{ value: string; label: string }[]>(
    []
  );
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [currentStep, setCurrentStep] = useState<FormStep>("personal");
  const [isPhoneValid, setIsPhoneValid] = useState<boolean>(false);
  const [showTCModal, setShowTCModal] = useState<boolean>(false);

  const initialForm: FormData = {
    fullName: "",
    emailId: "",
    phoneNumber: "",
    address: "",
    yearOfStudy: "",
    college: "",
    rollNumber: "",
    password: "",
    confirmPassword: "",
    roleId: STUDENT_ROLE_ID,
    userId: "",
    tcStatus: "false",
  };

  const [form, setForm] = useState<FormData>(initialForm);

  // Validate phone number on mount if any (defensive)
  useEffect(() => {
    if (form.phoneNumber) {
      const { valid } = formatPhoneNumber(form.phoneNumber);
      setIsPhoneValid(valid);
      setErrors((prev) => ({
        ...prev,
        phoneNumber: valid ? "" : "Please enter a valid phone number",
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formatPhoneNumber = (number: string, region = "IN") => {
    try {
      const parsed = phoneUtil.parseAndKeepRawInput(number, region);
      const valid = phoneUtil.isValidNumber(parsed);
      const formatted = phoneUtil.format(parsed, PhoneNumberFormat.NATIONAL);
      return { formatted, valid };
    } catch {
      return { formatted: number, valid: false };
    }
  };

  // Fetch colleges
  useEffect(() => {
    const fetchColleges = async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/college`, {
          headers: { "Content-Type": "application/json" },
          withCredentials: true,
        });

        if (Array.isArray(res.data?.success)) {
          const formatted = res.data.success
            .map((c: any) => ({
              value: c.collegeName,
              label: c.collegeName,
            }))
            .sort((a: any, b: any) => a.label.localeCompare(b.label));

          setColleges(formatted);
        }
      } catch {
        toast.error("Failed to load colleges");
      }
    };

    fetchColleges();
  }, []);

  const validateField = (name: keyof FormData, value: string) => {
    if (!value.trim()) return "This field is required";

    switch (name) {
      case "emailId":
        if (!validateEmail(value)) return "Please enter a valid email address";
        break;
      case "phoneNumber": {
        const { valid } = formatPhoneNumber(value);
        setIsPhoneValid(valid);
        if (!valid) return "Please enter a valid phone number";
        break;
      }
      case "password":
        if (value.length < 8) return "Password must be at least 8 characters";
        break;
      case "yearOfStudy":
        if (!/^\d{4}$/.test(value) || +value < 1980)
          return "Please select a valid year";
        break;
    }
    return "";
  };

  const validateCurrentStep = (): boolean => {
    const newErrors: Record<string, string> = {};
    let isValid = true;

    const stepFields: Record<FormStep, (keyof FormData)[]> = {
      personal: ["fullName", "emailId", "phoneNumber"],
      college: ["college", "yearOfStudy"],
      password: ["password", "confirmPassword"],
    };

    const currentFields = stepFields[currentStep];

    currentFields.forEach((field) => {
      if (field === "confirmPassword") {
        if (!form.confirmPassword?.trim()) {
          newErrors.confirmPassword = "Please confirm your password.";
          isValid = false;
        } else if (form.password !== form.confirmPassword) {
          newErrors.confirmPassword = "Passwords do not match.";
          isValid = false;
        }
      } else {
        const error = validateField(field, form[field]);
        if (error) isValid = false;
        newErrors[field] = error;
      }
    });

    setErrors(newErrors);
    return isValid;
  };

  const handleChange = (name: keyof FormData, value: string) => {
    setForm((prev) => ({ ...prev, [name]: value }));

    // Clear previous error for this field
    setErrors((prev) => ({ ...prev, [name]: "" }));

    // confirmPassword specific clearing
    if (name === "confirmPassword") {
      setErrors((prev) => ({ ...prev, confirmPassword: "" }));
    }

    // phone validation
    if (name === "phoneNumber") {
      const { valid } = formatPhoneNumber(value);
      setIsPhoneValid(valid);
      setErrors((prev) => ({
        ...prev,
        phoneNumber: valid ? "" : "Please enter a valid phone number",
      }));
    }

    // password / confirm password cross-check
    if (name === "password" && form.confirmPassword) {
      setErrors((prev) => ({
        ...prev,
        confirmPassword:
          value !== form.confirmPassword ? "Passwords do not match" : "",
      }));
    }

    if (name === "confirmPassword") {
      setErrors((prev) => ({
        ...prev,
        confirmPassword:
          value !== form.password ? "Passwords do not match" : "",
      }));
    }
  };

  const handleNextStep = () => {
    if (!validateCurrentStep()) {
      toast.error("Please fix the errors before proceeding");
      return;
    }
    const steps: FormStep[] = ["personal", "college", "password"];
    const currentIndex = steps.indexOf(currentStep);
    if (currentIndex < steps.length - 1) {
      setCurrentStep(steps[currentIndex + 1]);
    }
  };

  const handlePreviousStep = () => {
    const steps: FormStep[] = ["personal", "college", "password"];
    const currentIndex = steps.indexOf(currentStep);
    if (currentIndex > 0) setCurrentStep(steps[currentIndex - 1]);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // final validation
    if (!validateCurrentStep()) {
      toast.error("Please fix the errors in the form");
      return;
    }

    if (form.password !== form.confirmPassword) {
      setErrors((prev) => ({
        ...prev,
        confirmPassword: "Passwords do not match",
      }));
      toast.error("Passwords do not match");
      return;
    }

    // ensure T&C accepted
    if (form.tcStatus !== "true") {
      toast.error("Please accept Terms & Conditions to continue");
      return;
    }

    setLoading(true);
    try {
      const userData = {
        ...form,
        fullName: form.fullName.trim(),
        emailId: form.emailId.trim().toLowerCase(),
        phoneNumber: form.phoneNumber.trim(),
        address: form.address.trim(),
        yearOfStudy: form.yearOfStudy.trim(),
        college: form.college.trim(),
        rollNumber: form.rollNumber.trim(),
        tcStatus: form.tcStatus,
      } as Omit<FormData, "confirmPassword">;

      const success = await register(userData);
      if (success) {
        setForm(initialForm);
        setCurrentStep("personal");
        onSuccess?.();
      } else {
        toast.error("Registration failed. Please try again.");
      }
    } catch (error: any) {
      let message = "Registration failed. Please try again.";
      if (error?.response?.status === 409) {
        message = "Email or phone number already registered";
        setErrors((prev) => ({ ...prev, emailId: message }));
      } else if (error?.message) message = error.message;
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const isCurrentStepValid = () => {
    const stepFields: Record<FormStep, (keyof FormData)[]> = {
      personal: ["fullName", "emailId", "phoneNumber"],
      college: ["college", "yearOfStudy"],
      password: ["password", "confirmPassword"],
    };

    return stepFields[currentStep].every((field) => {
      if (field === "confirmPassword") {
        return (
          form.password === form.confirmPassword &&
          (form.confirmPassword?.trim() ?? "") !== "" &&
          !errors.confirmPassword
        );
      }
      return (form[field]?.trim() ?? "") !== "" && !errors[field];
    });
  };

  const handleTCModalClose = () => {
    setShowTCModal(false);
  };

  // Modal id used for aria-controls — ensure TCModal root has id="tc-modal"
  const TC_MODAL_ID = "tc-modal";

  return (
    <div className="w-full">
      <form onSubmit={handleSubmit} noValidate>
        {/* PERSONAL STEP */}
        {currentStep === "personal" && (
          <>
            <TextInput
              id="fullName"
              name="fullName"
              label="Name"
              type="text"
              value={form.fullName}
              onChange={(e) => handleChange("fullName", e.target.value)}
              placeholder="Enter your name"
              required
              errorMessage={errors.fullName}
              icon={LuUser}
              disabled={loading}
            />
            <TextInput
              id="emailId"
              name="emailId"
              label="Email Address"
              type="email"
              value={form.emailId}
              onChange={(e) => handleChange("emailId", e.target.value)}
              placeholder="Enter your email address"
              required
              errorMessage={errors.emailId}
              icon={FiMail}
              disabled={loading}
            />
            <PhoneNumberInput
              value={form.phoneNumber}
              onChange={(val) => handleChange("phoneNumber", val)}
              label="Mobile Number"
              required
              errorMessage={errors.phoneNumber}
              disabled={loading}
            />
            <TextInput
              id="address"
              name="address"
              label="Address"
              type="text"
              value={form.address}
              onChange={(e) => handleChange("address", e.target.value)}
              placeholder="Enter your complete address"
              errorMessage={errors.address}
              icon={LuMapPin}
              disabled={loading}
              optional
            />
          </>
        )}

        {/* COLLEGE STEP */}
        {currentStep === "college" && (
          <>
            <SelectInput
              name="college"
              label="College Name"
              value={form.college}
              onChange={(val) => handleChange("college", val)}
              required
              errorMessage={errors.college}
              disabled={loading}
              options={colleges}
              icon={LuBuilding2}
            />
            <SelectInput
              name="yearOfStudy"
              label="Year of Admission"
              value={form.yearOfStudy}
              onChange={(val) => handleChange("yearOfStudy", val)}
              required
              errorMessage={errors.yearOfStudy}
              disabled={loading}
              placeholder="Select your admission year"
              options={Array.from(
                { length: new Date().getFullYear() - 2015 + 1 },
                (_, i) => {
                  const yearVal = (2015 + i).toString();
                  return { label: yearVal, value: yearVal };
                }
              ).reverse()}
              icon={LuCalendar}
            />
            <TextInput
              id="rollNumber"
              name="rollNumber"
              label="Roll Number"
              type="text"
              value={form.rollNumber}
              onChange={(e) => handleChange("rollNumber", e.target.value)}
              placeholder="Enter your roll number"
              errorMessage={errors.rollNumber}
              icon={LiaIdCard}
              disabled={loading}
              optional
            />
          </>
        )}

        {/* PASSWORD STEP */}
        {currentStep === "password" && (
          <>
            <PasswordInput
              id="password"
              name="password"
              label="Password"
              value={form.password}
              onChange={(e) => handleChange("password", e.target.value)}
              placeholder="Enter your password"
              required
              errorMessage={errors.password}
              error={!!errors.password}
              strengthLabel={getPasswordStrength(form.password)}
              disabled={loading}
              showComplexity={true}
            />
            <PasswordInput
              id="confirmPassword"
              name="confirmPassword"
              label="Confirm Password"
              value={form.confirmPassword ?? ""}
              onChange={(e) => handleChange("confirmPassword", e.target.value)}
              placeholder="Confirm your password"
              required
              errorMessage={errors.confirmPassword}
              error={!!errors.confirmPassword}
              disabled={loading}
            />

            {/* T&C checkbox + link */}
            <div className="flex items-start gap-3 mb-6">
              <div className="flex-shrink-0">
                <input
                  type="checkbox"
                  id="acceptTC"
                  checked={form.tcStatus === "true"}
                  onChange={(e) =>
                    handleChange(
                      "tcStatus",
                      e.target.checked ? "true" : "false"
                    )
                  }
                  className="w-5 h-5 text-gray-600 rounded focus:ring-gray-500"
                  disabled={loading}
                />
              </div>

              <div className="text-gray-700">
                <label htmlFor="acceptTC" className="font-medium select-none">
                  I agree to the{" "}
                </label>

                {/* T&C open button (non-submitting) */}
                <div className="inline">
                  <button
                    type="button"
                    className="forgotPasswordLink cursor-pointer"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setShowTCModal(true);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setShowTCModal(true);
                      }
                    }}
                    aria-expanded={showTCModal}
                    aria-controls={TC_MODAL_ID}
                    disabled={loading}
                  >
                    Terms &amp; Conditions
                  </button>
                </div>

                {/* inline error for T&C if needed */}
                {form.tcStatus !== "true" && (
                  <div className="text-xs text-red-600 mt-1" role="alert">
                    You must accept the Terms & Conditions to register.
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        {/* BUTTONS */}
        <div className="flex gap-3 pt-2">
          {currentStep !== "personal" && (
            <button
              type="button"
              onClick={handlePreviousStep}
              className="flex items-center justify-center gap-2 px-4 py-2 text-gray-700 border border-gray-300 rounded-full hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={loading}
            >
              <LuArrowLeft size={16} />
              Back
            </button>
          )}

          {currentStep !== "password" ? (
            <button
              type="button"
              onClick={handleNextStep}
              disabled={
                (currentStep === "personal" && !isPhoneValid) ||
                !isCurrentStepValid() ||
                loading
              }
              className="flex items-center justify-center gap-2 flex-1 px-4 py-2 bg-myButtonBg text-white rounded-full hover:bg-myButtonBg/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
              <LuArrowRight size={16} />
            </button>
          ) : (
            // AuthButton should render a submit button (type="submit") internally.
            <AuthButton
              title="Complete Registration"
              loadingTitle="Registering..."
              loading={loading}
              disabled={
                !isCurrentStepValid() || form.tcStatus !== "true" || loading
              }
            />
          )}
        </div>
      </form>

      {/* TCModal — ensure its root element has id="tc-modal" to match aria-controls above */}
      <TCModal showModal={showTCModal} onClose={handleTCModalClose} />
    </div>
  );
};
